import { useCallback, useContext, useEffect, useState } from "react";
import { useCycleCountStore } from "../store/cycle-count";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardSubtitle,
  IonCardTitle,
  IonCol,
  IonGrid,
  IonRow,
  IonText,
} from "@ionic/react";
import { maxResults } from "../hooks/useQueryExec";
import CardFilter from "./CardFilter";
import { encodeParam } from "../util/ApiHelper";

/**
 * Offline read surface for downloaded cycle-count batches, shown on the Home
 * screen's offline tabs. Mirrors DownloadedWorkOrders / DownloadedEquipments:
 * reads from the persisted store and filters locally (no network).
 */
export default function DownloadedCycleCounts() {
  const downloadedBatches = useCycleCountStore(
    (state) => state.downloadedBatches
  );
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { translations } = useContext(TranslationsContext);
  const [searchText, setSearchText] = useState("");

  const [filteredRows, setFilteredRows] = useState(downloadedBatches);

  useEffect(() => {
    setFilteredRows(downloadedBatches);
  }, [downloadedBatches]);

  const hasDownloaded = downloadedBatches.length > 0;

  const handleFilter = useCallback(
    async (queryString: string) => {
      if (queryString) {
        // Case-insensitive substring match across the searchable fields
        // (replaces Fuse.js fuzzy matching, which returned near-miss values).
        const needle = queryString.toLowerCase();
        const res = downloadedBatches.filter((item) =>
          [
            item.name_0,
            item.batch_id_0,
            item.stores_location_0,
            item.status_0,
          ].some((value) =>
            String(value ?? "").toLowerCase().includes(needle)
          )
        );
        setFilteredRows(res.slice(0, maxResults));
      } else {
        setFilteredRows(downloadedBatches.slice(0, maxResults));
      }
      setSearchText(queryString);
    },
    [downloadedBatches]
  );

  return (
    <>
      <CardFilter
        hideFilter={downloadedBatches.length <= 0}
        queryRows={[
          {
            Columns: [
              {
                Hidden: false,
                Name: "Name",
                Order: 1,
                Text: "Text",
                Type: "",
                Value: "",
              },
            ],
            HeaderColor: "",
            HeaderName: "",
            HeaderRow: 0,
            HeaderText: "",
            HeaderType: "",
            HeaderValue: "",
          },
        ]}
        resultsFilter={searchText}
        filterMethod={handleFilter}
      />
      {hasDownloaded &&
        filteredRows.map((batch, index) => (
          <IonCard
            key={`downloaded-cc-${batch.batch_id_0}-${index}`}
            routerLink={`/CycleCount/${encodeParam(
              String(batch.batch_id_0)
            )}`}
          >
            <IonCardHeader>
              <IonCardTitle>{batch.name_0}</IonCardTitle>
              <IonCardSubtitle>{batch.status_0}</IonCardSubtitle>
            </IonCardHeader>
            <IonCardContent>
              <IonGrid>
                <IonRow hidden={!batch.batch_id_0}>
                  <IonCol>
                    {translations["lbl_incyclecountbatch_batchid"] ||
                      "Batch Id"}
                  </IonCol>
                  <IonCol>{batch.batch_id_0}</IonCol>
                </IonRow>
                <IonRow hidden={!batch.stores_location_0}>
                  <IonCol>
                    {translations[
                      "lbl_eqlocationsinquiryview_storeslocation"
                    ] || "Stores Location"}
                  </IonCol>
                  <IonCol>{batch.stores_location_0}</IonCol>
                </IonRow>
                <IonRow hidden={!batch.total_items_222}>
                  <IonCol>
                    {translations["lbl_invcyclecountcomputed_totalitems"] ||
                      "Total Items"}
                  </IonCol>
                  <IonCol>{batch.total_items_222}</IonCol>
                </IonRow>
              </IonGrid>
            </IonCardContent>
          </IonCard>
        ))}

      <IonText
        hidden={hasDownloaded}
        color={"default"}
        style={{ padding: "16px", display: "inline-block" }}
      >
        {translatedMessages["NoResultsFound"]?.MessageText ||
          "No results found."}
      </IonText>
    </>
  );
}
